import { useZero } from "@rocicorp/zero/react";
import { sharedMutators } from "../../../shared/mutators";
import { PERSONAL_CHAT_TIDY_DAYS } from "../../../shared/personalChatInactivity";
import { useChatWorkflowUrl } from "../../lib/chatWorkflowUrl";
import { routes, useMakeUrl } from "../../lib/urls";
import { TidyUpModal } from "../TidyUpModal";
import { useToast } from "../Toast";
import {
  ChatRowBadge,
  type ChatRowData,
  ChatRowSubtitle,
} from "./ChatRowParts";
import { chatLabel } from "./pickChatIconColor";

type Props = {
  open: boolean;
  onClose: () => void;
  rows: readonly { chat: ChatRowData; chatIds: string[] }[];
};

const count = (n: number, singular: string, plural: string) =>
  `${n} ${n === 1 ? singular : plural}`;

export function TidyUpChatsModal({ open, onClose, rows }: Props) {
  const z = useZero();
  const makeUrl = useMakeUrl();
  const chatWorkflowUrl = useChatWorkflowUrl();
  const { showToast } = useToast();

  const deleteRows = async (keys: string[]) => {
    onClose();
    const ids = rows
      .filter((row) => keys.includes(row.chat.id))
      .flatMap((row) => row.chatIds);
    const results = await Promise.allSettled(
      ids.map(
        async (id) =>
          await z.mutate(sharedMutators.deletePersonalChat({ id })).server
      )
    );
    if (
      results.some(
        (result) =>
          result.status === "rejected" || result.value.type === "error"
      )
    ) {
      showToast({
        icon: "circle-off",
        message: "Some chats couldn’t be deleted. Please try again.",
        buttonLabel: "Dismiss",
      });
    }
  };

  const workflows = rows.filter((row) => row.chat.workflow).length;
  const chats = rows.length - workflows;
  const listed = [
    chats > 0 && count(chats, "chat", "chats"),
    workflows > 0 && count(workflows, "workflow", "workflows"),
  ]
    .filter(Boolean)
    .join(" and ");
  const them = rows.length === 1 ? "it" : "them";
  const description = [
    `You have ${listed} that ${rows.length === 1 ? "is" : "are"} empty or unused.`,
    workflows > 0
      ? `Remove ${them} from Recent chats to keep things tidy. ${
          workflows === 1
            ? "The workflow and its branch conversations aren’t affected."
            : "The workflows and their branch conversations aren’t affected."
        }`
      : `Delete ${them} to keep things tidy.`,
    `You can restore ${them} from Deleted chats.`,
  ].join(" ");

  return (
    <TidyUpModal
      open={open}
      onClose={onClose}
      onConfirm={deleteRows}
      itemNoun={{ singular: "item", plural: "items" }}
      verb={workflows > 0 ? "Remove" : "Delete"}
      inactivityDays={PERSONAL_CHAT_TIDY_DAYS}
      description={rows.length > 0 ? description : undefined}
      selectable
      items={rows.map(({ chat }) => ({
        key: chat.id,
        href: chat.workflow
          ? chatWorkflowUrl(chat.id, chat.workflow)
          : makeUrl(routes.chats, { tabId: chat.id }),
        badge: <ChatRowBadge chat={chat} />,
        name: chat.workflow?.name ?? chatLabel(chat.name),
        subtitle: <ChatRowSubtitle chat={chat} />,
      }))}
    />
  );
}
